/**
 * Schémas Zod CRM — prospects.
 */
import { z } from 'zod';
import { CRM_COMPANY_SCOPES, CRM_PARTY_KINDS, PROSPECT_STATUSES } from '../enums/crm.enum.js';
import { normalizeFirstName, normalizeLastName, toFirstNameCase, toLastNameCase, } from '../utils/format.utils.js';
import { emailSchema } from './auth.schema.js';
import { paginationQuerySchema, searchSchema, uuidSchema } from './common.schema.js';
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
/** Champs d'identité du tiers : la casse saisie est normalisée (nom en capitales, prénom capitalisé). */
const optionalFirstName = optionalText(80).transform(normalizeFirstName);
const optionalLastName = optionalText(80).transform(normalizeLastName);
const crmContactInputSchema = z.object({
    firstName: z.string().trim().min(1).max(80).transform(toFirstNameCase),
    lastName: z.string().trim().min(1).max(80).transform(toLastNameCase),
    jobTitle: optionalText(80),
    email: emailSchema.nullable().optional(),
    mobilePhone: optionalText(30),
    landlinePhone: optionalText(30),
    secondaryPhone: optionalText(30),
    isPrimary: z.boolean().optional(),
});
const prospectBaseSchema = {
    kind: z.enum(CRM_PARTY_KINDS),
    companyScope: z.enum(CRM_COMPANY_SCOPES).nullable().optional(),
    companyName: optionalText(180),
    civility: optionalText(60),
    firstName: optionalFirstName,
    lastName: optionalLastName,
    email: emailSchema.nullable().optional(),
    mobilePhone: optionalText(30),
    landlinePhone: optionalText(30),
    secondaryPhone: optionalText(30),
    jobTitle: optionalText(80),
    addressLine1: optionalText(180),
    addressLine2: optionalText(180),
    postalCode: optionalText(16),
    city: optionalText(120),
    countryCode: optionalText(2),
    siren: optionalText(9),
    siret: optionalText(14),
    rnaNumber: optionalText(20),
    status: z.enum(PROSPECT_STATUSES).optional(),
    source: optionalText(60),
    score: z.number().int().min(0).max(100).nullable().optional(),
    sellerUserId: uuidSchema.nullable().optional(),
    assigneeUserId: uuidSchema.nullable().optional(),
    contacts: z.array(crmContactInputSchema).max(20).optional(),
};
function refineProspectIdentity(value, ctx) {
    if (value.kind === 'individual') {
        if (value.firstName === null ||
            value.firstName === undefined ||
            value.firstName.trim() === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['firstName'],
                message: 'Le prénom est obligatoire pour un prospect particulier.',
            });
        }
        if (value.lastName === null || value.lastName === undefined || value.lastName.trim() === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['lastName'],
                message: 'Le nom est obligatoire pour un prospect particulier.',
            });
        }
        return;
    }
    if (value.companyName === null ||
        value.companyName === undefined ||
        value.companyName.trim() === '') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['companyName'],
            message: 'La raison sociale est obligatoire pour ce type de prospect.',
        });
    }
    if (value.kind === 'company' &&
        (value.companyScope === null || value.companyScope === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['companyScope'],
            message: 'Le périmètre géographique de la société est obligatoire.',
        });
    }
}
/** Paramètres de `GET /prospects`. */
export const prospectListQuerySchema = paginationQuerySchema.extend({
    search: searchSchema,
    filter: z
        .object({
        status: z.enum(PROSPECT_STATUSES).optional(),
        sellerUserId: uuidSchema.optional(),
        assigneeUserId: uuidSchema.optional(),
        kind: z.enum(CRM_PARTY_KINDS).optional(),
    })
        .optional(),
});
/** Corps de `POST /prospects`. */
export const prospectCreateSchema = z
    .object(prospectBaseSchema)
    .superRefine(refineProspectIdentity);
/** Corps de `PATCH /prospects/:id`. */
export const prospectUpdateSchema = z
    .object({
    ...prospectBaseSchema,
    lastContactAt: z.string().datetime({ offset: true }).nullable().optional(),
})
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
export const lookupQuerySchema = z.object({ q: z.string().trim().min(2).max(120) });
//# sourceMappingURL=prospect.schema.js.map